<template>
	<div class="content-page content-page--service-settings">
		<ActionSetNav
			v-if="!loading"
			:name="this.$route.params.company.advanced.companyName"
			:display="true"
			:actions="mainNavActions"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Service Locations
				</h2>
			</div>
			<div class="content-page__actions">
				<MenuButton
					:iconBeforeLabel="true"
					iconClassName="icon-more"
					:items="locationTypes"
					v-model="locationSelected"
				/>
			</div>
		</div>
		<ScrollContainer
			v-if="!loading"
			class="content-page__content"
			:selectorArray="pageElements"
			:extraSpace="64"
		>
			<!-- <CompanyContent :data="companyLocalData" /> -->
			<Tabs
				:tabs="locationsTabs"
				v-if="serviceLocationsLocalData.length > 1"
			>
				<template
					v-for="(item, index) in serviceLocationsLocalData"
					#[slotName(index)]
				>
					{{ item.name }}
				</template>
				<template
					v-for="(item, index) in serviceLocationsLocalData"
					#[`content${slotName(index)}`]
				>
					<div
						class="service-locations service-locations--multiple"
						:key="index"
					>
						<Toggle
							v-model="expandAllSections[index]"
							@change="handleExpandSections(index)"
							label="Expand All Details"
							data-test="expandAllToggle"
						>
							<template slot="true-value">On</template>
							<template slot="false-value">Off</template>
						</Toggle>
						<!-- DEV NOTE: these first two sections (address and information) are also used in company settings so make sure the logic to save the data on this section outside these components -->
						<!-------- ADDRESS SECTION -------->
						<CompanyLocation
							ref="companyLocation"
							:data="item.data.location"
						/>
						<CompanyAddress
							ref="companyAddress"
							:data="item.data.address"
						/>
						<!-------- CONTACT INFORMATION SECTION -------->
						<CompanyInformation
							ref="companyInformation"
							:data="item.data.contactInfo"
						/>
						<!-------- GENERAL SECTION -------->
						<ServiceLocationsGeneral
							ref="serviceLocationsGeneral"
							:data="item.data.general"
						/>
						<ServiceLocationsFinancial
							ref="serviceLocationsFinancial"
							:data="item.data.financial"
						/>
						<ServiceLocationsDisclaimers
							ref="serviceLocationsDisclaimers"
							:data="item.data.disclaimers"
						/>
						<ServiceLocationsPrinting
							ref="serviceLocationsPrinting"
							:data="item.data.printing"
						/>
						<ServiceLocationsSignature
							ref="serviceLocationsSignature"
							:data="item.data.signatureCaptureMethops"
						/>
						<ServiceLocationsSmsEmail
							ref="serviceLocationsSmsEmail"
							:data="item.data.smsEmail"
						/>
					</div>
				</template>
			</Tabs>
			<div class="service-locations service-locations--single" v-else>
				<Toggle
					v-model="expandAllSectionsSingle"
					@change="handleExpandSectionsSingle"
					label="Expand All Details"
					data-test="expandAllToggle"
				>
					<template slot="true-value">On</template>
					<template slot="false-value">Off</template>
				</Toggle>
				<!-------- GENERAL SECTION -------->
				<ServiceLocationsGeneral
					ref="serviceLocationsGeneral"
					:data="serviceLocationsLocalData[0].data.general"
				/>
				<ServiceLocationsFinancial
					ref="serviceLocationsFinancial"
					:data="serviceLocationsLocalData[0].data.financial"
				/>
				<ServiceLocationsDisclaimers
					ref="serviceLocationsDisclaimers"
					:data="serviceLocationsLocalData[0].data.disclaimers"
				/>
				<ServiceLocationsPrinting
					ref="serviceLocationsPrinting"
					:data="serviceLocationsLocalData[0].data.printing"
				/>
				<ServiceLocationsSignature
					ref="serviceLocationsSignature"
					:data="
						serviceLocationsLocalData[0].data
							.signatureCaptureMethops
					"
				/>
				<ServiceLocationsSmsEmail
					ref="serviceLocationsSmsEmail"
					:data="serviceLocationsLocalData[0].data.smsEmail"
				/>
			</div>
		</ScrollContainer>
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import MenuButton from '@/components/forms/button/menu-button';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Tabs from '@/components/shells/tabs/tabs';
import CompanyLocation from '@/sections/companies/default-settings/sections/company-location';
import CompanyAddress from '@/sections/companies/default-settings/sections/company-address';
import CompanyInformation from '@/sections/companies/default-settings/sections/company-information';
import ServiceLocationsGeneral from './sections/service-locations-general';
import ServiceLocationsFinancial from './sections/service-locations-financial';
import ServiceLocationsDisclaimers from './sections/service-locations-disclaimers';
import ServiceLocationsPrinting from './sections/service-locations-printing';
import ServiceLocationsSignature from './sections/service-locations-signature';
import ServiceLocationsSmsEmail from './sections/service-locations-sms-email';
import { ApiService as api } from '@/services/axiosHelper';

export default {
	name: 'ServiceLocations',
	components: {
		ActionSetNav,
		Breadcrumbs,
		MenuButton,
		ScrollContainer,
		Tabs,
		CompanyLocation,
		CompanyAddress,
		CompanyInformation,
		ServiceLocationsGeneral,
		ServiceLocationsFinancial,
		ServiceLocationsDisclaimers,
		ServiceLocationsPrinting,
		ServiceLocationsSignature,
		ServiceLocationsSmsEmail
	},
	data() {
		return {
			expandAllSections: [],
			expandAllSectionsSingle: true,
			pageElements: [
				'header.site__header',
				'nav.action-set-nav',
				'footer.site__footer',
				'.content-page__heading'
			],
			loading: false,
			serviceLocationsLocalData: [],
			locationsTabs: [],
			locationTypes: [
				{
					text: 'Parts',
					value: 'P'
				},
				{
					text: 'Sales',
					value: 'S'
				},
				{
					text: 'Service',
					value: 'V'
				}
			],
			locationSelected: { text: 'Add Location', value: '' },
			mainNavActions: [
				{
					id: 1,
					action: {
						name: 'accounting'
					},
					title: 'Select to go to Accounting page',
					iconClass: 'icon icon-manage',
					label: 'Accounting',
					type: 'route-link',
					visible: true
				},
				{
					id: 2,
					action: {
						name: 'payroll'
					},
					title: 'Select to go to Payroll page',
					iconClass: 'icon icon-error',
					label: 'Payroll',
					type: 'route-link',
					visible: true
				},
				{
					id: 3,
					action: {
						name: 'parts'
					},
					iconClass: 'icon icon-customer-integration',
					label: 'Parts',
					title: 'Select to go to Parts Locations page',
					type: 'route-link',
					visible: true
				},
				{
					id: 4,
					action: '#',
					disabled: false,
					iconClass: 'icon icon-menu',
					label: '',
					title: 'select to see the more actions',
					type: 'dropdown',
					visible: true,
					items: [
						{
							id: 5,
							action: {
								name: 'sales'
							},
							disabled: false,
							iconClass: 'icon icon-document',
							label: 'Sales',
							title: 'Select to go to the Sales page',
							type: 'route-link',
							visible: true
						},
						{
							id: 6,
							action: {
								name: 'service'
							},
							disabled: false,
							iconClass: 'icon icon-document',
							label: 'Service',
							title: 'Select to go to the Service page',
							type: 'route-link',
							visible: true
						}
					]
				}
			],
			urlNames: [
				{
					name: 'Clients',
					data: {
						path: '/clients'
					}
				},
				{
					name: this.$route.params.company.advanced.companyName,
					data: {
						path: '/clients/:id/companies'
					}
				},
				{
					name: 'Service Locations'
				}
			]
		};
	},
	created() {
		this.loading = true;
		this.fetchServiceLocations();
	},
	methods: {
		slotName(index) {
			return index + 1;
		},
		/* DEV NOTE: use VUEX */
		async fetchServiceLocations() {
			try {
				const results = await api.get(
					`service-locations/${this.$route.params.id}`
				);

				results.forEach(element => {
					this.expandAllSections.push(true);

					this.serviceLocationsLocalData.push({
						name: element.name,
						data: element.data
					});
					this.locationsTabs.push({
						active: false,
						disabled: false
					});
					this.locationsTabs[0].active = true;
				});
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
			}
		},
		handleExpandSections(index) {
			this.$refs.companyLocation[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.companyAddress[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.companyInformation[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.serviceLocationsGeneral[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.serviceLocationsFinancial[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.serviceLocationsDisclaimers[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.serviceLocationsPrinting[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.serviceLocationsSignature[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.serviceLocationsSmsEmail[
				index
			].$children[0].open = this.expandAllSections[index];
		},
		handleExpandSectionsSingle() {
			this.$refs.serviceLocationsGeneral.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.serviceLocationsFinancial.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.serviceLocationsDisclaimers.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.serviceLocationsPrinting.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.serviceLocationsSignature.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.serviceLocationsSmsEmail.$children[0].open = this.expandAllSectionsSingle;
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_cards.scss';

.content-page {
	&__heading {
		display: flex;
		align-items: center;
	}
}

.service-locations {
	@include card;
	height: auto;
}
</style>
